'use client'

import type { MillsApiPaths } from '../../src/api/generated/schema'

const ALLOWED_ORIGINS = new Set([
  'https://stg-api.mills.allimediahub.com',
  'https://api.mills.allimediahub.com',
  'https://mills-web-production.fourth-sale.workers.dev',
])
const REQUEST_TIMEOUT_MS = 5_000

export const MILLS_QUERY_KEY = ['mills'] as const
export const IDENTITY_QUERY_POLICY = {
  refreshInterval: 60_000,
  staleTime: 0,
  gcTime: 60_000,
  persister: undefined,
} as const

export type PublicMillsPath = Extract<keyof MillsApiPaths, `/api/v1/public/${string}`>

type GetOperation<Path extends PublicMillsPath> = Exclude<MillsApiPaths[Path]['get'], undefined>
type PublicMillsResponse<Path extends PublicMillsPath> =
  GetOperation<Path> extends {
    responses: { 200: { content: { 'application/json': infer Response } } }
  }
    ? Response
    : never
type PublicMillsGetPath = {
  [Path in PublicMillsPath]: PublicMillsResponse<Path> extends never ? never : Path
}[PublicMillsPath]
type OperationParameters<Path extends PublicMillsGetPath> =
  GetOperation<Path> extends {
    parameters: infer Parameters
  }
    ? Parameters
    : never
type PathParameters<Path extends PublicMillsGetPath> =
  OperationParameters<Path> extends {
    path: infer Parameters
  }
    ? Parameters
    : never
type QueryParameters<Path extends PublicMillsGetPath> =
  OperationParameters<Path> extends {
    query?: infer Parameters
  }
    ? Parameters
    : never

type PublicRequestOptions<Path extends PublicMillsGetPath> = {
  baseUrl?: string
  fetchImpl?: (request: Request) => Promise<Response>
  method?: 'GET'
  pathParams?: PathParameters<Path>
  query?: QueryParameters<Path>
  signal?: AbortSignal
}

export class MillsPublicClientError extends Error {
  constructor(
    public readonly code: string,
    public readonly status?: number,
    options?: ErrorOptions
  ) {
    super(code, options)
    this.name = 'MillsPublicClientError'
  }
}

export function configuredMillsPublicOrigin(): string | undefined {
  const origin = process.env.NEXT_PUBLIC_MILLS_API_ORIGIN
  return origin && ALLOWED_ORIGINS.has(origin) ? origin : undefined
}

function requiredOrigin(baseUrl?: string) {
  const configured = baseUrl ?? process.env.NEXT_PUBLIC_MILLS_API_ORIGIN
  if (!configured || !ALLOWED_ORIGINS.has(configured)) {
    throw new MillsPublicClientError('MILLS_PUBLIC_ORIGIN_INVALID')
  }
  return configured
}

function publicUrl(
  path: string,
  baseUrl: string,
  pathParams?: Record<string, string>,
  query?: Record<string, unknown>
) {
  if (!path.startsWith('/api/v1/public/')) {
    throw new MillsPublicClientError('MILLS_PUBLIC_PATH_INVALID')
  }
  const resolvedPath = path.replace(/\{([^}]+)\}/g, (_token, name: string) => {
    const value = pathParams?.[name]
    if (!value) throw new MillsPublicClientError('MILLS_PUBLIC_PATH_PARAMS_INVALID')
    return encodeURIComponent(value)
  })
  const url = new URL(resolvedPath, baseUrl)
  if (
    url.origin !== baseUrl ||
    !url.pathname.startsWith('/api/v1/public/') ||
    url.username ||
    url.password ||
    url.hash
  ) {
    throw new MillsPublicClientError('MILLS_PUBLIC_PATH_INVALID')
  }
  for (const [name, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(name, String(value))
  }
  return url
}

export async function requestMillsPublic<Path extends PublicMillsGetPath>(
  path: Path,
  options: PublicRequestOptions<Path> = {}
): Promise<PublicMillsResponse<Path>> {
  if (typeof window === 'undefined') {
    throw new MillsPublicClientError('MILLS_PUBLIC_BROWSER_ONLY')
  }
  if ((options.method ?? 'GET') !== 'GET') {
    throw new MillsPublicClientError('MILLS_PUBLIC_METHOD_INVALID')
  }

  const baseUrl = requiredOrigin(options.baseUrl)
  const url = publicUrl(
    path,
    baseUrl,
    options.pathParams as Record<string, string> | undefined,
    options.query as Record<string, unknown> | undefined
  )
  const controller = new AbortController()
  let timedOut = false
  const timeout = window.setTimeout(() => {
    timedOut = true
    controller.abort(new MillsPublicClientError('MILLS_PUBLIC_TIMEOUT'))
  }, REQUEST_TIMEOUT_MS)
  const abortFromCaller = () => controller.abort(options.signal?.reason)
  options.signal?.addEventListener('abort', abortFromCaller, { once: true })

  try {
    const request = new Request(url, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      headers: { accept: 'application/json' },
      signal: controller.signal,
    })
    const response = await (options.fetchImpl ?? fetch)(request)
    if (!response.ok) {
      throw new MillsPublicClientError('MILLS_PUBLIC_RESPONSE_ERROR', response.status)
    }
    return (await response.json()) as PublicMillsResponse<Path>
  } catch (error) {
    if (timedOut) {
      throw new MillsPublicClientError('MILLS_PUBLIC_TIMEOUT', undefined, {
        cause: error,
      })
    }
    throw error
  } finally {
    window.clearTimeout(timeout)
    options.signal?.removeEventListener('abort', abortFromCaller)
  }
}
